import React, { useEffect, useState } from "react";
import { SectionHeading, SecondaryButton, ErrorBanner, EmptyState } from "./ui";
import { listPoojas } from "../api/poojaApi";
import PoojaDetail from "./PoojaDetail";
import PoojaBookingHistory from "./PoojaBookingHistory";

/**
 * Entry point for the "Pooja with Pandit Ji" tab.
 *
 * Three views, switched locally (no router needed for a single tab):
 *   "list"    -> grid of poojas (this file)
 *   "detail"  -> PoojaDetail.jsx, opened by tapping a card
 *   "history" -> PoojaBookingHistory.jsx, opened via "My Bookings" button
 */
export default function PoojaTab() {
  const [view, setView] = useState("list");
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [poojas, setPoojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (view !== "list") return;
    setLoading(true);
    setError(null);
    listPoojas()
      .then((res) => setPoojas(res.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [view]);

  if (view === "detail" && selectedSlug) {
    return (
      <PoojaDetail
        slug={selectedSlug}
        onBack={() => setView("list")}
      />
    );
  }

  if (view === "history") {
    return <PoojaBookingHistory onBack={() => setView("list")} />;
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <SectionHeading
          eyebrow="Live Pooja Booking"
          title="Pooja with Pandit Ji"
          subtitle="Book an experienced Pandit Ji for your religious ceremony — Kaal Sarp Dosh, Griha Pravesh, Satyanarayan Katha, and more."
        />
        <SecondaryButton onClick={() => setView("history")}>
          📜 My Bookings
        </SecondaryButton>
      </div>

      {loading && <div style={styles.loading}>Loading poojas…</div>}
      <ErrorBanner message={error} />

      {!loading && !error && poojas.length === 0 && (
        <EmptyState>No poojas are available right now — please check back soon.</EmptyState>
      )}

      <div style={styles.grid}>
        {poojas.map((p) => (
          <button
            key={p.id}
            style={styles.card}
            onClick={() => {
              setSelectedSlug(p.slug);
              setView("detail");
            }}
          >
            {p.thumbnail_url ? (
              <img src={p.thumbnail_url} alt={p.title} style={styles.thumb} />
            ) : (
              <div style={{ ...styles.thumb, ...styles.thumbFallback }}>🪔</div>
            )}
            <div style={styles.cardBody}>
              {p.discount_percent > 0 && (
                <span style={styles.discountBadge}>{p.discount_percent}% Off</span>
              )}
              <h3 style={styles.cardTitle}>{p.title}</h3>
              <div style={styles.priceRow}>
                <span style={styles.price}>₹{p.price}</span>
                {p.original_price > p.price && (
                  <span style={styles.originalPrice}>₹{p.original_price}</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 1000, margin: "0 auto" },
  headerRow: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    flexWrap: "wrap", gap: 12,
  },
  loading: { textAlign: "center", color: "var(--ink-soft)", padding: "20px 0" },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18, marginTop: 8,
  },
  card: {
    textAlign: "left", padding: 0, border: "1px solid var(--line)",
    borderRadius: "var(--radius-lg)", background: "var(--card)", cursor: "pointer",
    overflow: "hidden", boxShadow: "var(--shadow-card)",
  },
  thumb: { width: "100%", height: 140, objectFit: "cover", display: "block" },
  thumbFallback: {
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 40, background: "var(--paper-2)",
  },
  cardBody: { padding: "14px 16px 16px" },
  discountBadge: {
    display: "inline-block", background: "var(--success)", color: "#fff",
    fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
    marginBottom: 8,
  },
  cardTitle: {
    fontFamily: "var(--font-serif)", fontSize: 18, margin: "0 0 8px", color: "var(--ink)",
  },
  priceRow: { display: "flex", alignItems: "baseline", gap: 8 },
  price: { fontSize: 18, fontWeight: 700, color: "var(--terracotta-dark)" },
  originalPrice: { fontSize: 13, color: "var(--ink-soft)", textDecoration: "line-through" },
};
